import React, { useCallback, useContext, useMemo, useRef } from 'react'
import styled from 'styled-components'
import FormatCurrency from 'components/Common/FormatCurrency'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import { BusinessTravellerBudgetInfoModal } from './BusinessTravellerBudgetInfoModal'
import ModalContext from 'contexts/ModalContext'
import Clickable from 'components/Common/Clickable/Clickable'
import moment from 'moment'
import Expander from 'components/utils/Expander'
import PageBanner from 'LuxKit/Banners/PageBanner'
import useBusinessTravellerBusinessBudgetFetcher from 'businessTraveller/hooks/useBusinessTravellerBusinessBudgetFetcher'
import { useHeaderStateObserver } from 'contexts/HeaderStateContext'

const StickyContainer = styled.div`
  position: sticky;
  top: 0;
`

interface Props {
  checkInDate?: string;
  checkOutDate?: string;
  roomCount?: number;
  className?: string;
  offer?: App.AnyOffer;
}

function BusinessTravellerStickyBudgetBar(props: Props) {
  const {
    checkInDate,
    checkOutDate,
    roomCount = 1,
    className,
    offer,
  } = props
  const numberOfNights = useMemo(() => {
    if (!checkInDate || !checkOutDate) {
      return
    }
    return moment(checkOutDate).diff(checkInDate, 'days')
  }, [checkInDate, checkOutDate])

  const { budgetData } = useBusinessTravellerBusinessBudgetFetcher(offer)

  const moreThanOneBudget = budgetData?.budgets ? budgetData.budgets?.length > 1 : false
  const maxBudget = (budgetData?.maxNightlyBudget ?? 0) * (numberOfNights ?? 0)
  const minBudget = (budgetData?.minNightlyBudget ?? 0) * (numberOfNights ?? 0)
  const showMinBudget = maxBudget !== minBudget
  const isBudgetBarShown = !!maxBudget

  const showModal = useContext(ModalContext)
  const toggleModal = useCallback(() => {
    if (budgetData?.budgets) {
      showModal(<BusinessTravellerBudgetInfoModal
        budgets={budgetData.budgets}
      />)
    }
  }, [budgetData?.budgets, showModal])

  // this sets the height of the budgetbar into the headerHeight context
  // allowing us to accurately position other headers around it using that height
  const budgetBarRef = useRef(null)
  useHeaderStateObserver('businessTravellerBudgetBar', budgetBarRef, isBudgetBarShown)

  if (!isBudgetBarShown) {
    return null
  }

  return <StickyContainer className={className} ref={budgetBarRef}>
    <Expander expanded={budgetData?.status === 'success'}>
      <PageBanner
        kind="promotional-primary"
        align="center"
        description={<b>
          Accommodation budget:{' '}
          {!!showMinBudget && <><FormatCurrency value={minBudget} format="dynamicDollar" /> to{' '}</>}
          <FormatCurrency value={maxBudget} format="dynamicDollar" /> per room {roomCount > 1 && <>(<FormatCurrency value={roomCount * maxBudget} format="dynamicDollar" /> total)</>}
        </b>}
        trailingContent={moreThanOneBudget && <Clickable onClick={toggleModal}>
          <LineInfoCircleIcon size="S" />
        </Clickable>}
      />
    </Expander>
  </StickyContainer>
}

export default BusinessTravellerStickyBudgetBar
